(() => {
  const shared = window.CAFThemeHotfixShared;
  if (!shared) return;

  const {
    CART_PREVIEW_CHECKOUT_SELECTOR,
    CART_PREVIEW_MUTATION_SELECTOR,
    MOBILE_POPUP_BREAKPOINT,
    POPUP_MODAL_SURFACE_SELECTOR,
    RECOMATIC_CARD_SELECTOR,
    TEEINBLUE_EVENT_COMPONENT_INJECTED,
    createRafThrottled,
    extractFirstInteger,
    formatReviewCountText,
    getSyntheticJudgeMeDisplayRating,
    isCartContext,
    isProductContext,
    mutationsContainAddedNode,
    seededIntegerInRange,
    setImportantStyles,
    runWithDelays,
  } = shared;

  const recomaticProductCache = new Map();
  const recomaticProductInFlight = new Map();
  let recomaticProductTypeByTag = null;
  const CART_PAYLOAD_CACHE_TTL = 2000;
  const CART_PREVIEW_OPENING_CLASS = 'caf-cart-preview-opening';
  const CART_PREVIEW_POPUP_MODAL_SELECTOR = [
    '.vm--modal.tee-preview-popup',
    '.vm--container.tee-preview-popup .vm--modal',
    '.tee-preview-popup .vm--modal',
  ].join(', ');
  const RECOMATIC_FALLBACK_TYPE_LABELS = new Set([
    'best seller',
    'best sellers',
    'going fast',
    'hot pick',
    'popular choice',
    'trending',
    'trending now',
  ]);

  const normalizeStorefrontRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const runWhenIdle = (callback, delay = 0, timeout = 2000) => {
    const run = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(callback, { timeout });
        return;
      }
      window.setTimeout(callback, 0);
    };

    if (delay > 0) {
      window.setTimeout(run, delay);
      return;
    }

    run();
  };

  let cartPayloadCache = null;
  let cartPayloadCacheExpiresAt = 0;
  let cartPayloadCacheVersion = 0;
  let cartPayloadPromise = null;

  const clearCartPayloadCache = () => {
    cartPayloadCache = null;
    cartPayloadCacheExpiresAt = 0;
    cartPayloadCacheVersion += 1;
    cartPayloadPromise = null;
  };

  const normalizeCartImageUrl = (url) => {
    if (!url) return '';
    let value = String(url).trim();
    if (!value) return '';

    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }

    value = value
      .replace(/\\\//g, '/')
      .replace(/^https:\\\//i, 'https://')
      .replace(/^http:\\\//i, 'http://');

    if (value.startsWith('\\/')) value = value.slice(1);

    if (/%[0-9A-Fa-f]{2}/.test(value)) {
      try {
        value = decodeURIComponent(value);
      } catch (error) {
        // keep original value when decode fails
      }
    }

    if (value.startsWith('//')) return `${window.location.protocol}${value}`;
    return value;
  };

  const isLikelyCartImageUrl = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    if (!text) return false;
    if (/^data:image\//i.test(text)) return true;
    if (/^blob:/i.test(text)) return true;
    if (!/^https?:|^\/\/|^\//i.test(text)) return false;
    if (/\.(avif|gif|jpe?g|png|svg|webp)(\?|$)/i.test(text)) return true;
    return /(cdn|cloudfront|shopify|teeinblue|image|preview|mockup|thumbnail|render)/i.test(text);
  };

  const extractCartImageFromString = (input, depth = 0) => {
    if (!input || depth > 2) return '';

    const text = String(input).trim();
    if (!text) return '';

    const normalized = normalizeCartImageUrl(text);
    if (isLikelyCartImageUrl(normalized)) return normalized;

    if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
      try {
        const parsed = JSON.parse(text);
        const parsedFound = findCartImageUrlInObject(parsed, 0);
        if (parsedFound) return parsedFound;
      } catch (error) {
        // no-op
      }
    }

    if (/%[0-9A-Fa-f]{2}/.test(text)) {
      try {
        const decoded = decodeURIComponent(text);
        const decodedFound = extractCartImageFromString(decoded, depth + 1);
        if (decodedFound) return decodedFound;
      } catch (error) {
        // no-op
      }
    }

    const dataImageMatch = text.match(/data:image\/[a-zA-Z0-9+.-]+;base64,[a-zA-Z0-9+/=]+/);
    if (dataImageMatch?.[0]) return dataImageMatch[0];

    const urlMatch = text.match(/https?:\/\/[^\s"'<>]+/i);
    if (urlMatch?.[0]) {
      const url = normalizeCartImageUrl(urlMatch[0]);
      if (isLikelyCartImageUrl(url)) return url;
    }

    return '';
  };

  function findCartImageUrlInObject(source, depth = 0) {
    if (!source || depth > 4) return '';

    if (typeof source === 'string') {
      const extracted = extractCartImageFromString(source);
      return extracted || '';
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        const found = findCartImageUrlInObject(item, depth + 1);
        if (found) return found;
      }
      return '';
    }

    if (typeof source !== 'object') return '';

    const prioritizedKeys = [
      '_customization_image',
      '_customization_preview',
      '_customization',
      '_preview',
      '_preview_image',
      '_preview_url',
      '_tb_preview',
      '_tb_image',
      'customization_image',
      'customizationImage',
      'customizationPreview',
      'customization_preview',
      'preview',
      'preview_image',
      'previewImage',
      'preview_url',
      'previewUrl',
      'mockup_image',
      'mockupImage',
      'thumbnail',
      'image',
      'src',
      'url',
    ];

    for (const key of prioritizedKeys) {
      if (!(key in source)) continue;
      const found = findCartImageUrlInObject(source[key], depth + 1);
      if (found) return found;
    }

    const entries = Object.entries(source);

    for (const [key, value] of entries) {
      if (!/(image|preview|thumbnail|mockup|render)/i.test(key)) continue;
      const found = findCartImageUrlInObject(value, depth + 1);
      if (found) return found;
    }

    for (const [, value] of entries) {
      const found = findCartImageUrlInObject(value, depth + 1);
      if (found) return found;
    }

    return '';
  }

  const uniqueCartImageUrls = (values) => {
    const seen = new Set();
    return values
      .map((value) => normalizeCartImageUrl(value))
      .filter((value) => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      });
  };

  const fetchCartPayload = async () => {
    const now = Date.now();
    if (cartPayloadCache && now < cartPayloadCacheExpiresAt) return cartPayloadCache;
    if (cartPayloadPromise) return cartPayloadPromise;

    const requestVersion = cartPayloadCacheVersion;
    const request = window
      .fetch(`${normalizeStorefrontRoot()}cart.js`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (payload && requestVersion === cartPayloadCacheVersion) {
          cartPayloadCache = payload;
          cartPayloadCacheExpiresAt = Date.now() + CART_PAYLOAD_CACHE_TTL;
        }
        return payload;
      })
      .catch(() => null);

    cartPayloadPromise = request;
    request.finally(() => {
      if (cartPayloadPromise === request) {
        cartPayloadPromise = null;
      }
    });

    return request;
  };

  const getCartLineImageCandidates = (item) => {
    const propertyImage = findCartImageUrlInObject(item?.properties || {});
    const featuredImage = normalizeCartImageUrl(item?.featured_image?.url || item?.featured_image?.src || '');
    const lineImage = normalizeCartImageUrl(item?.image || item?.featured_image || '');

    return uniqueCartImageUrls([propertyImage, featuredImage, lineImage]).filter(isLikelyCartImageUrl);
  };

  const applyCartLinePreviewImage = (cartItemNode, previewUrl) => {
    if (!(cartItemNode instanceof Element) || !previewUrl) return;

    const imageNode = cartItemNode.querySelector('.cart_page_image img');
    if (!(imageNode instanceof HTMLImageElement)) return;
    if (imageNode.dataset.cafCartPreviewSrc === previewUrl) return;

    const wrapNode = cartItemNode.querySelector('.cart_page_image .image-element__wrap');
    const preloadImage = new Image();
    preloadImage.decoding = 'async';

    preloadImage.onload = () => {
      imageNode.src = previewUrl;
      imageNode.removeAttribute('srcset');
      imageNode.removeAttribute('sizes');
      imageNode.style.width = '100%';
      imageNode.style.maxWidth = '100%';
      imageNode.style.maxHeight = 'none';
      imageNode.style.height = 'auto';
      imageNode.style.objectFit = 'contain';
      imageNode.dataset.cafCartPreviewSrc = previewUrl;

      if (wrapNode instanceof HTMLElement) {
        wrapNode.style.display = 'block';
        wrapNode.style.width = '100%';
        wrapNode.style.maxWidth = '100%';
        wrapNode.style.maxHeight = 'none';
        wrapNode.style.height = 'auto';
      }
    };

    preloadImage.src = previewUrl;
  };

  const normalizeCartLinePreviewImages = async () => {
    if (!isCartContext()) return;

    const cart = await fetchCartPayload();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (items.length === 0) return;

    document.querySelectorAll('#cart_form [data-cart-item]').forEach((cartItemNode) => {
      const lineId = Number(cartItemNode.getAttribute('data-line-id') || 0);
      if (!Number.isFinite(lineId) || lineId <= 0) return;

      const lineItem = items[lineId - 1];
      if (!lineItem) return;

      const candidates = getCartLineImageCandidates(lineItem);
      if (candidates.length === 0) return;

      applyCartLinePreviewImage(cartItemNode, candidates[0]);
    });
  };

  const initCartLinePreviewImages = () => {
    if (!isCartContext()) return;

    const cartForm = document.querySelector('#cart_form');
    const run = () => {
      normalizeCartLinePreviewImages();
    };
    const scheduleRun = createRafThrottled(run);
    const scheduleRefresh = (delays = [0, 240, 900, 1800], { refreshCart = false } = {}) => {
      if (refreshCart) clearCartPayloadCache();
      delays.forEach((delay) => {
        window.setTimeout(() => {
          scheduleRun();
        }, delay);
      });
    };

    scheduleRefresh();

    if (window.__cafCartLinePreviewImagesBound) return;
    window.__cafCartLinePreviewImagesBound = true;

    if (cartForm instanceof Element) {
      const observer = new MutationObserver((mutations) => {
        const shouldRefresh =
          mutationsContainAddedNode(mutations, '#cart_form [data-cart-item], #cart_form .cart_page_image img') ||
          mutations.some((mutation) => {
            const target = mutation.target;
            if (!(target instanceof Element)) return false;
            if (!target.closest('#cart_form')) return false;
            return !target.closest('.tee-cart-preview-slot, .tee-edit-customization, .cart__item-image-actions');
          });

        if (!shouldRefresh) return;
        scheduleRefresh([0, 120, 480], { refreshCart: true });
      });

      observer.observe(cartForm, { childList: true, subtree: true });
    }

    document.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, () => scheduleRefresh([0, 240, 900]));
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('#cart_form [data-cart-quantity-input]')) return;
        scheduleRefresh([120, 480, 1200], { refreshCart: true });
      },
      true,
    );
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
          !target.closest(
            '#cart_form .js-change-quantity, #cart_form [data-remove-item], #cart_form .cart__remove-btn, #cart_form .tee-edit-customization',
          )
        ) {
          return;
        }

        scheduleRefresh([120, 480, 1200], { refreshCart: true });
      },
      true,
    );
  };

  const initCartRemoveConfirmation = () => {
    if (!isCartContext()) return;
    if (window.__cafCartRemoveConfirmationBound) return;

    const modal = document.getElementById('caf-cart-remove-confirm');
    const dialog = modal?.querySelector('.caf-cart-remove-confirm__dialog');
    const image = modal?.querySelector('[data-cart-remove-confirm-image]');
    const confirmButton = modal?.querySelector('[data-cart-remove-confirm-remove]');
    const closeButtons = modal?.querySelectorAll('[data-cart-remove-confirm-close]');

    if (!(modal instanceof HTMLElement) || !(dialog instanceof HTMLElement) || !(confirmButton instanceof HTMLElement)) {
      return;
    }

    window.__cafCartRemoveConfirmationBound = true;

    let activeRemoveButton = null;
    let previousActiveElement = null;

    const getCartItemImage = (cartItem) => {
      const imageNode = cartItem?.querySelector('.cart_page_image img, .cart__item--image img');
      if (!(imageNode instanceof HTMLImageElement)) return { src: '', alt: '' };

      return {
        src: imageNode.currentSrc || imageNode.src || '',
        alt: imageNode.alt || '',
      };
    };

    const closeModal = () => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('caf-cart-remove-confirm-open');
      document.body?.classList.remove('caf-cart-remove-confirm-open');

      if (image instanceof HTMLImageElement) {
        image.hidden = true;
        image.removeAttribute('src');
        image.alt = '';
      }

      const restoreFocusElement = previousActiveElement;
      activeRemoveButton = null;
      previousActiveElement = null;

      if (restoreFocusElement instanceof HTMLElement && document.contains(restoreFocusElement)) {
        restoreFocusElement.focus({ preventScroll: true });
      }
    };

    const openModal = (removeButton) => {
      const cartItem = removeButton.closest('#cart_form [data-cart-item]');
      const imageState = getCartItemImage(cartItem);

      activeRemoveButton = removeButton;
      previousActiveElement = document.activeElement;

      if (image instanceof HTMLImageElement && imageState.src) {
        image.src = imageState.src;
        image.alt = imageState.alt;
        image.hidden = false;
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('caf-cart-remove-confirm-open');
      document.body?.classList.add('caf-cart-remove-confirm-open');
      dialog.focus({ preventScroll: true });
    };

    const confirmRemove = () => {
      const removeButton = activeRemoveButton;
      if (!(removeButton instanceof HTMLElement)) {
        closeModal();
        return;
      }

      removeButton.dataset.cafRemoveConfirmed = 'true';
      closeModal();
      removeButton.click();
    };

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const removeButton = target.closest('#cart_form [data-remove-item]');
        if (!(removeButton instanceof HTMLElement)) return;

        if (removeButton.dataset.cafRemoveConfirmed === 'true') {
          delete removeButton.dataset.cafRemoveConfirmed;
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        openModal(removeButton);
      },
      true,
    );

    closeButtons?.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    confirmButton.addEventListener('click', confirmRemove);

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }
    });
  };

  const extractProductHandleFromLink = (href) => {
    if (!href) return '';
    try {
      const url = new URL(href, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const productIndex = parts.indexOf('products');
      if (productIndex < 0 || !parts[productIndex + 1]) return '';
      return decodeURIComponent(parts[productIndex + 1]).trim();
    } catch (error) {
      return '';
    }
  };

  const getRecomaticProductHandleFromCard = (card) => {
    if (!(card instanceof Element)) return '';
    const linkNode = card.querySelector('a.recomatic-content[href], a[href*="/products/"]');
    return extractProductHandleFromLink(linkNode?.getAttribute('href') || linkNode?.href || '');
  };

  const getRecomaticProductPayload = async (handle) => {
    if (!handle) return null;
    if (recomaticProductCache.has(handle)) return recomaticProductCache.get(handle);
    if (recomaticProductInFlight.has(handle)) return recomaticProductInFlight.get(handle);

    const endpoint = `${normalizeStorefrontRoot()}products/${encodeURIComponent(handle)}.js`;
    const request = window
      .fetch(endpoint, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        recomaticProductCache.set(handle, payload);
        return payload;
      })
      .catch(() => {
        recomaticProductCache.set(handle, null);
        return null;
      })
      .finally(() => {
        recomaticProductInFlight.delete(handle);
      });

    recomaticProductInFlight.set(handle, request);
    return request;
  };

  const getSecondaryProductImage = async (handle) => {
    const payload = await getRecomaticProductPayload(handle);
    const images = Array.isArray(payload?.images) ? payload.images : [];
    return images.length > 1 ? images[1] : null;
  };

  const getRecomaticProductTypeByTag = () => {
    if (recomaticProductTypeByTag) return recomaticProductTypeByTag;

    const map = new Map();
    const mappingNode = document.getElementById('caf-product-tag-mappings');
    const mappingText = mappingNode?.textContent?.trim();

    if (mappingText) {
      try {
        const mappings = JSON.parse(mappingText);
        Object.entries(mappings).forEach(([tag, label]) => {
          const normalizedTag = String(tag || '').trim().toLowerCase();
          const normalizedLabel = String(label || '').trim();
          if (normalizedTag && normalizedLabel) {
            map.set(normalizedTag, normalizedLabel);
          }
        });
      } catch (error) {
        // Product type labels can still fall back to the app value.
      }
    }

    recomaticProductTypeByTag = map;
    return recomaticProductTypeByTag;
  };

  const normalizeProductTags = (tags) => {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag || '').trim()).filter(Boolean);
    return String(tags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  const getMappedProductTypeFromPayload = (payload) => {
    const productTypeByTag = getRecomaticProductTypeByTag();
    const tags = normalizeProductTags(payload?.tags);

    for (const tag of tags) {
      const mappedType = productTypeByTag.get(tag.toLowerCase());
      if (mappedType) return mappedType;
    }

    return String(payload?.type || '').trim();
  };

  const isRecomaticFallbackTypeLabel = (value) => {
    const text = String(value || '').trim().toLowerCase();
    return Boolean(text && RECOMATIC_FALLBACK_TYPE_LABELS.has(text));
  };

  const isUsableRecomaticTypeLabel = (value) => {
    const text = String(value || '').trim();
    return Boolean(text && !isRecomaticFallbackTypeLabel(text));
  };

  const hidePendingRecomaticType = (typeNode) => {
    if (!(typeNode instanceof HTMLElement)) return;
    typeNode.style.visibility = 'hidden';
    typeNode.setAttribute('aria-hidden', 'true');
  };

  const hideRecomaticType = (typeNode) => {
    if (!(typeNode instanceof HTMLElement)) return;
    typeNode.style.display = 'none';
    typeNode.style.visibility = '';
    typeNode.setAttribute('aria-hidden', 'true');
  };

  const showRecomaticType = (typeNode, label) => {
    if (!(typeNode instanceof HTMLElement)) return;
    typeNode.textContent = label;
    typeNode.style.display = '';
    typeNode.style.visibility = '';
    typeNode.removeAttribute('aria-hidden');
  };

  const applyMappedTypeToRecomaticCard = async (card, typeNode) => {
    if (!(card instanceof Element) || !(typeNode instanceof HTMLElement)) return;

    const currentLabel = typeNode.textContent || '';
    if (isUsableRecomaticTypeLabel(currentLabel)) {
      // Avoid product JSON fetches when Recomatic already rendered a real product type.
      typeNode.dataset.cafRecomaticTypeResolved = 'dom';
      showRecomaticType(typeNode, currentLabel.trim());
      return;
    }

    const linkNode = card.querySelector('a.recomatic-content[href], a[href*="/products/"]');
    const handle = extractProductHandleFromLink(linkNode?.getAttribute('href') || linkNode?.href);

    if (isRecomaticFallbackTypeLabel(currentLabel)) {
      hidePendingRecomaticType(typeNode);
    }

    if (!handle) {
      if (isRecomaticFallbackTypeLabel(currentLabel)) hideRecomaticType(typeNode);
      return;
    }

    const payload = await getRecomaticProductPayload(handle);
    const mappedType = getMappedProductTypeFromPayload(payload);

    if (mappedType) {
      showRecomaticType(typeNode, mappedType);
      return;
    }

    if (isRecomaticFallbackTypeLabel(currentLabel)) {
      hideRecomaticType(typeNode);
    }
  };

  const applySecondaryImageToRecomaticCard = async (card) => {
    if (!card || card.dataset.cafRecomaticImageProcessed === 'true') return;

    const imageNode = card.querySelector('img.recomatic-image');
    const linkNode = card.querySelector('a.recomatic-content[href], a[href*="/products/"]');
    if (!imageNode || !linkNode) return;

    const handle = extractProductHandleFromLink(linkNode.getAttribute('href') || linkNode.href);
    if (!handle) return;

    card.dataset.cafRecomaticImageProcessed = 'true';
    const secondaryImage = await getSecondaryProductImage(handle);
    if (!secondaryImage) return;

    imageNode.src = secondaryImage;
    imageNode.removeAttribute('srcset');
    imageNode.setAttribute('data-caf-secondary-image', 'true');
  };

  const applySecondaryImagesToRecomaticCards = (scope = document) => {
    scope.querySelectorAll(RECOMATIC_CARD_SELECTOR).forEach((card) => {
      applySecondaryImageToRecomaticCard(card);
    });
  };

  const normalizeRecomaticCardMeta = (card) => {
    if (!(card instanceof Element)) return;

    const attributesNode = card.querySelector('.recomatic-attributes');
    const titleNode = attributesNode?.querySelector('.recomatic-title');
    const priceWrapNode = attributesNode?.querySelector('.recomatic-price-wrap');
    let ratingsNode = attributesNode?.querySelector('.recomatic-ratings');
    const typeNode = attributesNode?.querySelector('.recomatic-type') || priceWrapNode?.querySelector('.recomatic-type');

    if (!(attributesNode instanceof Element) || !(titleNode instanceof Element)) return;

    if (typeNode instanceof HTMLElement) {
      applyMappedTypeToRecomaticCard(card, typeNode);
      if (typeNode.parentElement !== attributesNode || typeNode.nextElementSibling !== titleNode) {
        attributesNode.insertBefore(typeNode, titleNode);
      }
    }

    if (!(ratingsNode instanceof HTMLElement)) {
      ratingsNode = document.createElement('div');
      ratingsNode.className = 'recomatic-ratings caf-recomatic-review-badge';
    }

    const handle = getRecomaticProductHandleFromCard(card);
    const reviewSeed = handle || titleNode.textContent || card.dataset.productId || '';
    const existingReviewCount = extractFirstInteger?.(ratingsNode.textContent);
    const syntheticReviewCount =
      existingReviewCount && existingReviewCount > 0
        ? existingReviewCount
        : seededIntegerInRange(`caf-recomatic-review-count:${reviewSeed}`, 25, 75);
    const displayRating =
      typeof getSyntheticJudgeMeDisplayRating === 'function' ? getSyntheticJudgeMeDisplayRating(reviewSeed) : '5.0';
    const ratingShell =
      ratingsNode.querySelector('.jdgm-prev-badge') ||
      Object.assign(document.createElement('div'), { className: 'jdgm-prev-badge' });

    ratingShell.dataset.cafPreviewReviewFormat = 'rating-count';
    ratingShell.innerHTML = formatReviewCountText(syntheticReviewCount, '', displayRating);
    ratingShell.setAttribute('aria-label', `${displayRating} out of 5 stars, ${syntheticReviewCount.toLocaleString('en-US')} reviews`);

    // Recomatic can leave native Judge.me siblings beside the theme badge after async rerenders.
    // Keep the bridge authoritative so price/review rows do not show duplicate app review text.
    ratingsNode.replaceChildren(ratingShell);

    ratingsNode.classList.add('jdgm-widget', 'jdgm-preview-badge', 'caf-recomatic-review-badge');
    ratingsNode.removeAttribute('aria-hidden');

    let priceReviewRow = Array.from(attributesNode.children).find((child) =>
      child.classList?.contains('recomatic-price-review-row'),
    );

    if (!priceReviewRow) {
      priceReviewRow = document.createElement('div');
      priceReviewRow.className = 'product-card-price-review-row recomatic-price-review-row';
      priceReviewRow.dataset.cafSharedProductCardRow = 'true';
    } else {
      priceReviewRow.classList.add('product-card-price-review-row', 'recomatic-price-review-row');
    }

    if (priceWrapNode instanceof Element) {
      // Recomatic can prepend "From" for non-TeeInBlue products; CAF cards use the actual price only.
      priceWrapNode.querySelectorAll('.recomatic-from').forEach((node) => node.remove());
      priceWrapNode.classList.add('product-card-price-slot', 'recomatic-price-slot');
      priceReviewRow.appendChild(priceWrapNode);
    }

    if (ratingsNode instanceof Element) {
      ratingsNode.classList.add('product-card-review-slot', 'recomatic-review-slot');
      priceReviewRow.appendChild(ratingsNode);
    }

    if (priceReviewRow.childElementCount === 0) return;
    if (priceReviewRow.parentElement !== attributesNode || priceReviewRow.previousElementSibling !== titleNode) {
      titleNode.insertAdjacentElement('afterend', priceReviewRow);
    }

    card.dataset.cafRecomaticCardLayout = 'shared-product-card';
  };

  const normalizeRecomaticCardsMeta = (scope = document) => {
    scope.querySelectorAll(RECOMATIC_CARD_SELECTOR).forEach((card) => {
      normalizeRecomaticCardMeta(card);
    });
  };

  const initRecomaticCardMeta = () => {
    const process = () => normalizeRecomaticCardsMeta(document);
    const scheduleProcess = createRafThrottled(process);

    process();
    runWithDelays(process, [200, 800, 1800]);

    if (window.__cafRecomaticCardMetaBound) return;
    window.__cafRecomaticCardMetaBound = true;

    const observer = new MutationObserver((mutations) => {
      if (
        !mutationsContainAddedNode(mutations, RECOMATIC_CARD_SELECTOR) &&
        !mutationsContainAddedNode(
          mutations,
          '.recomatic-price-wrap, .recomatic-type, .recomatic-title, .recomatic-ratings, .recomatic-price-review-row',
        )
      ) {
        return;
      }

      scheduleProcess();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('recomatic:loaded', process);
    document.addEventListener('recomatic:updated', process);
  };

  const initRecomaticSecondaryImages = () => {
    const process = () => applySecondaryImagesToRecomaticCards(document);
    // Secondary images require Shopify product JSON per card; run after initial render/interaction work.
    const scheduleProcess = createRafThrottled(() => runWhenIdle(process, 1200, 2500));

    runWhenIdle(process, 1600, 3000);

    if (window.__cafRecomaticSecondaryImagesObserverBound) return;
    window.__cafRecomaticSecondaryImagesObserverBound = true;

    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, RECOMATIC_CARD_SELECTOR)) return;
      scheduleProcess();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const decorateCartPreviewPopup = (modal, checkoutButton = null) => {
    if (!(modal instanceof Element)) return;

    modal.classList.add('caf-cart-preview-modal');
    if (modal instanceof HTMLElement) {
      setImportantStyles(modal, {
        animation: 'none',
        transition: 'none',
      });
    }

    const container = modal.closest('.vm--container');
    if (container) {
      container.classList.add('caf-cart-preview-container');
      if (container instanceof HTMLElement) {
        setImportantStyles(container, {
          animation: 'none',
          transition: 'none',
        });
      }
      const overlay = container.querySelector('.vm--overlay');
      if (overlay instanceof HTMLElement) {
        setImportantStyles(overlay, {
          animation: 'none',
          transition: 'none',
        });
      }
    }

    const wrapper =
      checkoutButton?.closest('.tee-preview-button__wrapper') || modal.querySelector('.tee-preview-button__wrapper');
    if (wrapper) {
      wrapper.classList.add('caf-cart-preview-actions');
    }

    const closeButton = modal.querySelector(
      '.vm--close, .tee-dialog-close, button[aria-label*="close" i], [title*="close" i]',
    );
    if (closeButton) {
      closeButton.classList.add('caf-cart-preview-close');
    }

    modal.querySelectorAll('img, canvas, svg, video').forEach((mediaNode) => {
      mediaNode.classList.add('caf-cart-preview-media');
    });
  };

  const applyCartPreviewPopupClasses = (scope = document) => {
    if (!scope) return;

    const checkoutButtons =
      scope instanceof Element && scope.matches(CART_PREVIEW_CHECKOUT_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(CART_PREVIEW_CHECKOUT_SELECTOR) || []);

    const previewModals =
      scope instanceof Element && scope.matches(CART_PREVIEW_POPUP_MODAL_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(CART_PREVIEW_POPUP_MODAL_SELECTOR) || []);

    previewModals.forEach((modal) => decorateCartPreviewPopup(modal));

    checkoutButtons.forEach((checkoutButton) => {
      if (!(checkoutButton instanceof Element)) return;
      checkoutButton.classList.add('caf-cart-preview-checkout');

      const modal =
        checkoutButton.closest('.vm--modal') ||
        checkoutButton.closest('.tee-dialog') ||
        checkoutButton.closest('[role="dialog"]');

      decorateCartPreviewPopup(modal, checkoutButton);
    });
  };

  const updateCartPreviewSlotState = () => {
    if (!isCartContext()) return;

    document.querySelectorAll('#cart_form .tee-cart-preview-slot').forEach((slot) => {
      const actionWrapper = slot.closest('.cart__item-image-actions');
      const hasPreviewButton = Boolean(
        slot.querySelector('.tee-preview-button, a.tee-preview-button') ||
          actionWrapper?.querySelector('.tee-preview-button, a.tee-preview-button'),
      );

      slot.classList.toggle('is-ready', hasPreviewButton);
      actionWrapper?.classList.toggle('is-ready', hasPreviewButton);
      if (slot.hasAttribute('aria-busy')) {
        slot.setAttribute('aria-busy', hasPreviewButton ? 'false' : 'true');
      }
    });
  };

  const moveCartPreviewButtonsToActionRows = (scope = document) => {
    if (!isCartContext()) return;

    const closestCartItem =
      scope instanceof Element
        ? scope.matches('#cart_form [data-cart-item]')
          ? scope
          : scope.closest('#cart_form [data-cart-item]')
        : null;
    const cartItems = closestCartItem
      ? [closestCartItem]
      : Array.from(scope.querySelectorAll?.('#cart_form [data-cart-item]') || []);

    cartItems.forEach((cartItem) => {
      if (!(cartItem instanceof Element)) return;
      const slot = cartItem.querySelector('.cart__item-preview-target .tee-cart-preview-slot');
      if (!(slot instanceof Element)) return;

      const previewButtons = Array.from(cartItem.querySelectorAll('.tee-preview-button, a.tee-preview-button')).filter(
        (button) => button instanceof Element,
      );
      if (previewButtons.length === 0) return;

      const getMovablePreviewNode = (button) => {
        const wrapper = button.closest('.tee-preview-button__wrapper');
        return wrapper instanceof Element && cartItem.contains(wrapper) ? wrapper : button;
      };

      const primaryPreviewButton = previewButtons.find((button) => slot.contains(button)) || previewButtons[0];
      const nodeToMove = getMovablePreviewNode(primaryPreviewButton);
      if (!slot.contains(nodeToMove)) {
        slot.appendChild(nodeToMove);
      }

      const keptNodes = new Set([nodeToMove]);
      previewButtons.slice(1).forEach((button) => {
        const duplicateNode = getMovablePreviewNode(button);
        if (
          !(duplicateNode instanceof Element) ||
          keptNodes.has(duplicateNode) ||
          duplicateNode.contains(nodeToMove)
        ) {
          return;
        }
        keptNodes.add(duplicateNode);
        duplicateNode.remove();
      });
    });
  };

  const initCartPreviewPopupTheme = () => {
    if (!isCartContext()) return;

    const run = (scope = document) => applyCartPreviewPopupClasses(scope);
    const scheduleRun = createRafThrottled(run);
    const markPreviewOpening = () => {
      document.body?.classList.add(CART_PREVIEW_OPENING_CLASS);
      window.setTimeout(() => document.body?.classList.remove(CART_PREVIEW_OPENING_CLASS), 900);
    };
    const handlePreviewOpenIntent = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        !target.closest(
          '#cart_form .tee-preview-button, #cart_form a.tee-preview-button, .cart__item--image .tee-preview-button, .tee-cart-preview-slot .tee-preview-button',
        )
      ) {
        return;
      }
      markPreviewOpening();
      runWithDelays(run, [0, 24, 120, 360]);
    };

    run(document);
    runWithDelays(run, [500, 1500]);

    if (window.__cafCartPreviewPopupThemeBound) return;
    window.__cafCartPreviewPopupThemeBound = true;

    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, CART_PREVIEW_MUTATION_SELECTOR)) return;
      run(document);
      scheduleRun(document);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, () => scheduleRun(document));
    document.addEventListener('pointerdown', handlePreviewOpenIntent, true);
    document.addEventListener('click', handlePreviewOpenIntent, true);
  };

  const normalizeCartPreviewButtonLabels = (scope = document) => {
    if (!isCartContext()) return;
    moveCartPreviewButtonsToActionRows(scope);

    const cartPreviewButtonSelector =
      '#cart_form .cart__item-image-actions .tee-preview-button, #cart_form .cart__item-image-actions a.tee-preview-button, #cart_form .cart__item--image .tee-preview-button, #cart_form .cart__item--image a.tee-preview-button';
    const buttons =
      scope instanceof Element && scope.matches(cartPreviewButtonSelector)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(cartPreviewButtonSelector) || []);

    buttons.forEach((button) => {
      if (!(button instanceof Element)) return;
      button.querySelectorAll('svg, i').forEach((icon) => icon.setAttribute('aria-hidden', 'true'));

      const textSpans = Array.from(button.querySelectorAll('span')).filter((span) => {
        const text = span.textContent?.trim() || '';
        return text && !(span.getAttribute('class') || '').includes('icon');
      });

      const labelNode =
        textSpans.find((span) => /preview/i.test(span.textContent || '')) ||
        textSpans[textSpans.length - 1] ||
        null;

      if (labelNode) {
        labelNode.textContent = 'Preview';
        return;
      }

      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
      );
      if (textNode) {
        textNode.textContent = 'Preview';
      }
    });
  };

  const normalizeCartEditButtonLabels = (scope = document) => {
    if (!isCartContext()) return;

    const editButtonSelector = '#cart_form .tee-edit-customization :is(a, button, .tee-btn)';
    const buttons =
      scope instanceof Element && scope.matches(editButtonSelector)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(editButtonSelector) || []);

    buttons.forEach((button) => {
      if (!(button instanceof Element)) return;
      button.querySelectorAll('svg, i').forEach((icon) => icon.setAttribute('aria-hidden', 'true'));

      const textSpans = Array.from(button.querySelectorAll('span')).filter((span) => {
        const text = span.textContent?.trim() || '';
        return text && !(span.getAttribute('class') || '').includes('icon');
      });

      const labelNode =
        textSpans.find((span) => /edit/i.test(span.textContent || '')) ||
        textSpans[textSpans.length - 1] ||
        null;

      if (labelNode) {
        labelNode.textContent = 'Edit';
        return;
      }

      const textNode = Array.from(button.childNodes).find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
      );
      if (textNode) {
        textNode.textContent = 'Edit';
        return;
      }

      button.append(document.createTextNode('Edit'));
    });
  };

  const initCartPreviewButtonLabelNormalize = () => {
    if (!isCartContext()) return;

    const run = (scope = document) => {
      normalizeCartPreviewButtonLabels(scope);
      normalizeCartEditButtonLabels(scope);
      updateCartPreviewSlotState();
    };
    const scheduleRun = createRafThrottled(run);

    run(document);
    runWithDelays(run, [300, 900, 1800]);

    if (window.__cafCartPreviewButtonLabelNormalizeBound) return;
    window.__cafCartPreviewButtonLabelNormalizeBound = true;

    const observer = new MutationObserver((mutations) => {
      if (
        !mutationsContainAddedNode(
          mutations,
          '.tee-preview-button, a.tee-preview-button, .tee-edit-customization, .tee-edit-customization a, .tee-edit-customization button, .tee-edit-customization .tee-btn',
        )
      ) {
        return;
      }
      scheduleRun(document);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, () => scheduleRun(document));
  };

  const isMobileViewport = () => {
    if (!window.matchMedia) return window.innerWidth <= MOBILE_POPUP_BREAKPOINT;
    return window.matchMedia(`(max-width: ${MOBILE_POPUP_BREAKPOINT}px)`).matches;
  };

  const MOBILE_POPUP_CONTAINER_CLASS = 'caf-mobile-popup-container';
  const MOBILE_POPUP_MODAL_CLASS = 'caf-mobile-popup-modal';
  const MOBILE_POPUP_OPEN_CLASS = 'caf-mobile-popup-open';
  const MOBILE_POPUP_CART_CLASS = 'caf-mobile-popup-cart';

  const isVisibleElement = (element) => {
    if (!(element instanceof Element)) return false;
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const clearMobilePopupClasses = () => {
    document.querySelectorAll(`.${MOBILE_POPUP_CONTAINER_CLASS}`).forEach((container) => {
      container.classList.remove(MOBILE_POPUP_CONTAINER_CLASS, MOBILE_POPUP_CART_CLASS);
    });
    document.querySelectorAll(`.${MOBILE_POPUP_MODAL_CLASS}`).forEach((modal) => {
      modal.classList.remove(MOBILE_POPUP_MODAL_CLASS, MOBILE_POPUP_CART_CLASS);
    });
    document.documentElement.classList.remove(MOBILE_POPUP_OPEN_CLASS);
    document.body?.classList.remove(MOBILE_POPUP_OPEN_CLASS);
  };

  const closeMiniCartIfOpen = () => {
    document.querySelectorAll('.cart-container.active_link').forEach((container) => {
      container.classList.remove('active_link');
    });
    document.body?.classList.remove('blocked-scroll');
  };

  const isTeeinbluePopupModal = (modal) => {
    if (!(modal instanceof Element)) return false;
    if (modal.matches('.tee-edit-popup, .tee-dialog-gallery, .tee-preview-popup, .caf-cart-preview-modal')) return true;
    if (
      modal.closest(
        '.vm--container.tee-edit-popup, .vm--container.tee-dialog-gallery, .vm--container.tee-preview-popup, .vm--container.caf-cart-preview-container, .tee-dialog-gallery, .tee-edit-popup, .tee-preview-popup, .caf-cart-preview-container, .tee-dialog',
      )
    ) {
      return true;
    }
    return Boolean(
      modal.querySelector(
        '.tee-dcontent, .tee-preview-button__checkout, .tee-gallery-content, .custom_modal_preview--modify_notice, .tee-dialog-close',
      ),
    );
  };

  const isCartPreviewPopupModal = (modal) => {
    if (!(modal instanceof Element)) return false;
    if (!isCartContext()) return false;
    if (modal.matches('.tee-preview-popup, .caf-cart-preview-modal')) return true;
    if (modal.closest('.vm--container.tee-preview-popup, .tee-preview-popup, .vm--container.caf-cart-preview-container')) return true;
    if (modal.querySelector('.tee-preview-button__checkout')) return true;
    if (modal.matches('.tee-edit-popup')) return false;
    const hasMedia = Boolean(modal.querySelector('img, canvas, svg, video'));
    const hasClose = Boolean(
      modal.querySelector('.vm--close, .tee-dialog-close, [aria-label*="close" i], [title*="close" i]'),
    );
    return hasMedia && hasClose;
  };

  const collectCandidatePopupModals = (scope = document) => {
    if (!scope) return [];
    if (scope instanceof Element && scope.matches('.vm--modal')) return [scope];
    return Array.from(scope.querySelectorAll?.('.vm--modal') || []);
  };

  const syncMobilePopupViewportState = (scope = document) => {
    clearMobilePopupClasses();

    if (!isMobileViewport()) return;

    let hasOpenPopup = false;
    const modals = collectCandidatePopupModals(scope);

    modals.forEach((modal) => {
      if (!isTeeinbluePopupModal(modal)) return;

      const container = modal.closest('.vm--container');
      const isCartPopup = isCartContext();
      const isCartPreview = isCartPreviewPopupModal(modal);

      modal.classList.add(MOBILE_POPUP_MODAL_CLASS);
      if (container) {
        container.classList.add(MOBILE_POPUP_CONTAINER_CLASS);
        const overlay = container.querySelector('.vm--overlay');
        if (overlay instanceof HTMLElement) {
          setImportantStyles(overlay, {
            transition: 'none',
            animation: 'none',
          });
        }
      }

      if (isCartPopup || isCartPreview) {
        modal.classList.add(MOBILE_POPUP_CART_CLASS);
        container?.classList.add(MOBILE_POPUP_CART_CLASS);
      }

      if (isVisibleElement(modal)) {
        hasOpenPopup = true;
      }
    });

    document.documentElement.classList.toggle(MOBILE_POPUP_OPEN_CLASS, hasOpenPopup);
    document.body?.classList.toggle(MOBILE_POPUP_OPEN_CLASS, hasOpenPopup);

    if (hasOpenPopup) {
      closeMiniCartIfOpen();
    }
  };

  const initMobilePopupViewportFix = () => {
    if (!isProductContext() && !isCartContext()) return;

    const run = (scope = document) => syncMobilePopupViewportState(scope);
    const scheduleRun = createRafThrottled(run);
    const runBurst = () => {
      run(document);
      [24, 120, 360, 720].forEach((delay) => {
        window.setTimeout(() => run(document), delay);
      });
    };

    runBurst();

    if (window.__cafMobilePopupViewportFixBound) return;
    window.__cafMobilePopupViewportFixBound = true;

    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, POPUP_MODAL_SURFACE_SELECTOR)) return;
      run(document);
      scheduleRun(document);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, runBurst);
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
          !target.closest(
            '.tee-btn--preview, .tee-edit-customization, .tee-preview-button, .tee-preview-button__checkout, .vm--close, .tee-dialog-close, .vm--overlay',
          )
        ) {
          return;
        }
        runBurst();
      },
      true,
    );

    window.addEventListener('resize', () => scheduleRun(document), { passive: true });
    window.addEventListener('orientationchange', runBurst, { passive: true });
  };

  const runCommerceHotfixes = () => {
    initRecomaticCardMeta();
    initRecomaticSecondaryImages();
    initCartRemoveConfirmation();
    initCartLinePreviewImages();
    initCartPreviewPopupTheme();
    initCartPreviewButtonLabelNormalize();
    initMobilePopupViewportFix();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runCommerceHotfixes);
  } else {
    runCommerceHotfixes();
  }
})();
